// @flow

// Effect Manager
import {
  fork,
  put,
  take,
  select,
  call,
  all,
} from 'redux-saga/effects'

// Libs
import _isEmpty from 'lodash/isEmpty'

// Selectors
import operations$ from '../selectors/operations'
import operation$ from '../selectors/operation'
import { firstAvailableGrowerId$ } from 'fc-assets'

// Action Types
import {
  MIGRATE_ASSET,
  SUBMIT_MIGRATE_ASSET,
} from '../constants'

// Constants
import {
  OPERATION_STATE,
  PROP_FARM,
  PROP_AVAILABLE_SOURCE_SEASONS,
  PROP_SOURCE_SEASON,
  // PROP_DESTINATION_SELECTED_SEASON,
  PROP_AVAILABLE_DESTINATION_SEASONS,
  FIELDS_TO_MIGRATE,
} from 'hfty-model-asset-migrate'
import {
  FIELD_URL,
  SUBFIELD_URL,
} from 'fc-model-assets'

// Operator
import assetMigrateOperator from 'hfty-operator-asset-migrate'

// Utils
import { getToken } from 'fc-auth'
import getUrl from 'hfty-utils/url'
import traverse from 'fc-utils/traverse'

// Controller
import jobsCtrl from 'fc-ctrl-sampling-jobs'
import assetCtrl from 'fc-ctrl-assets'
import gridCtrl from 'hfty-ctrl-grid'

export default function *watchOrderOperations (): Generator<*, void, *> {
  while (true) {
    const { type } = yield take([
      MIGRATE_ASSET,
      SUBMIT_MIGRATE_ASSET,
    ])

    switch (type) {
      case MIGRATE_ASSET: yield fork(handleMigrateAsset); break
      case SUBMIT_MIGRATE_ASSET: yield fork(handleSubmitMigrateAsset); break
    }
  }
}

export function *handleMigrateAsset (): Generator<*, void, *> {
  const operations = yield select(operations$)
  const action = yield assetMigrateOperator.actionCreators.cancelOperations(operations)
  yield put(action)

  const operation = assetMigrateOperator.create({
    model: {
      [OPERATION_STATE]: {
        [PROP_FARM]: null,
        [PROP_SOURCE_SEASON]: null,
        // [PROP_DESTINATION_SELECTED_SEASON]: null,
        [PROP_AVAILABLE_SOURCE_SEASONS]: [],
        [PROP_AVAILABLE_DESTINATION_SEASONS]: [],
        [FIELDS_TO_MIGRATE]: [],
      },
      isLoading: false,
    },
  })

  yield put(assetMigrateOperator.actionCreators.add(operation))
}

export function *handleSubmitMigrateAsset (): Generator<*, void, *> {
  const operations = yield select(operations$)
  const operation = yield select(operation$)
  const { sourceSeason, destinationSeason, fieldsToMigrate } = operation.model[OPERATION_STATE]

  const fields = fieldsToMigrate
    .filter((field: Object): Object => field.isSelected)
    .map((field: Object): Object => ({ id: field.id, shape: field.shape, grid: field.grid }))

  const token = yield call(getToken)
  const url = getUrl({
    path: `hefty/seasonmigration/`,
    // excludeTrailingSlash: true,
  })

  if (sourceSeason && destinationSeason && fieldsToMigrate) {
    const withLoader = {
      ...operation,
      model: {
        ...operation.model,
        isLoading: true,
      },
    }

    const actionWithLoader = assetMigrateOperator.actionCreators.updateOperation(withLoader)
    yield put(actionWithLoader)

    const response = yield call(fetch, url, {
      method: 'POST',
      headers: {
        Authorization: `Token ${token}`,
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fields: [ ...fields ],
        previous_season: sourceSeason.id,
        next_season: destinationSeason.id,
      }),
    })

    const { errors, error } = yield response.json()
    let nextOperation

    if (!error && _isEmpty(errors)) {
      const rootAsset = yield select(firstAvailableGrowerId$)
      const fieldsToMigrate = traverse(['model', OPERATION_STATE, FIELDS_TO_MIGRATE])(operation)
      const isJobMigrated = fieldsToMigrate.some((field: Object): boolean => field.grid)
      nextOperation = {
        ...operation,
        submitting: false,
        success: true,
      }

      yield all([
        call(assetCtrl.read, {
          url: FIELD_URL,
          queryParams: {
            shape: 'true',
            rootAsset,
          },
        }),
        call(assetCtrl.read, {
          url: SUBFIELD_URL,
          queryParams: {
            rootAsset,
          },
        }),
      ])

      if (isJobMigrated) {
        yield call(jobsCtrl.read, {
          queryParams: {
            rootAsset,
          },
        })

        yield call(gridCtrl.read, {
          queryParams: {
            rootAsset,
          },
        })
      }

      yield put(assetMigrateOperator.actionCreators.cancelOperations(operations))
    } else {
      nextOperation = {
        ...operation,
        model: {
          ...operation.model,
          isLoading: false,
        },
        errors: error || errors,
        success: false,
      }
      const action = assetMigrateOperator.actionCreators.updateOperation(nextOperation)

      yield put(action)
    }

    return nextOperation
  }
}
